import React, { useState, useEffect, useCallback } from 'react';
import { partyAPI, settingsAPI, gstinAPI, employeeAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Plus, Trash2, Edit, Users, Search, Settings, RefreshCw, Merge, ScanSearch, ArrowRight, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';

export default function PartyManagement() {
  const { user } = useAuth();
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [formDialog, setFormDialog] = useState({ open: false, editing: null });
  const [form, setForm] = useState({ name: '', type: 'general', gstin: '', address: '', mobile: '', email: '', state: '', assigned_to: '' });
  const [saving, setSaving] = useState(false);
  const [gstinLoading, setGstinLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: '' });
  const [settingsDialog, setSettingsDialog] = useState(false);
  const [numbering, setNumbering] = useState({ receipt_prefix: 'VLPL/LR', invoice_prefix: 'VLPL/INV' });
  const [syncing, setSyncing] = useState(false);
  const [mergeDialog, setMergeDialog] = useState(false);
  const [mergeKeep, setMergeKeep] = useState('');
  const [mergeDrop, setMergeDrop] = useState('');
  const [merging, setMerging] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [dupDialog, setDupDialog] = useState(false);
  const [scanningDups, setScanningDups] = useState(false);
  const isAdmin = user?.role === 'admin';

  const loadParties = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (typeFilter) params.type = typeFilter;
      const res = await partyAPI.list(params);
      setParties(res.data || []);
    } catch { toast.error('Failed to load parties'); }
    finally { setLoading(false); }
  }, [search, typeFilter]);

  useEffect(() => { loadParties(); }, [loadParties]);

  useEffect(() => {
    if (isAdmin) {
      settingsAPI.getNumbering().then(r => setNumbering(r.data)).catch(() => {});
      employeeAPI.list().then(r => setEmployees(r.data || [])).catch(() => {});
    }
  }, [isAdmin]);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (formDialog.editing) {
        await partyAPI.update(formDialog.editing, form);
        toast.success('Party updated');
      } else {
        await partyAPI.create(form);
        toast.success('Party created');
      }
      setFormDialog({ open: false, editing: null });
      loadParties();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await partyAPI.delete(deleteDialog.id);
      toast.success('Party deleted');
      setDeleteDialog({ open: false, id: '' });
      loadParties();
    } catch { toast.error('Delete failed'); }
  };

  const saveNumbering = async () => {
    try {
      await settingsAPI.updateNumbering(numbering);
      toast.success('Numbering format updated');
      setSettingsDialog(false);
    } catch { toast.error('Failed to save'); }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await partyAPI.syncFromTransactions();
      const count = res.data?.synced || 0;
      if (count > 0) {
        toast.success(`Synced ${count} new parties from transactions`);
        loadParties();
      } else {
        toast.info('All parties are already in sync');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Sync failed'); }
    finally { setSyncing(false); }
  };

  const handleMerge = async () => {
    if (!mergeKeep || !mergeDrop) { toast.error('Select both parties'); return; }
    if (mergeKeep === mergeDrop) { toast.error('Cannot merge a party with itself'); return; }
    setMerging(true);
    try {
      const res = await partyAPI.merge({ keep_id: mergeKeep, merge_id: mergeDrop });
      toast.success(res.data?.message || 'Parties merged');
      setMergeDialog(false);
      setMergeKeep('');
      setMergeDrop('');
      loadParties();
    } catch (e) { toast.error(e.response?.data?.detail || 'Merge failed'); }
    finally { setMerging(false); }
  };

  const handleFindDuplicates = async () => {
    setScanningDups(true);
    try {
      const res = await partyAPI.findDuplicates();
      const dups = res.data?.duplicates || [];
      setDuplicates(dups);
      setDupDialog(true);
      if (dups.length === 0) toast.info('No duplicates found — all party names are unique');
    } catch (e) { toast.error(e.response?.data?.detail || 'Scan failed'); }
    finally { setScanningDups(false); }
  };

  const handleQuickMerge = async (keepId, mergeId) => {
    try {
      const res = await partyAPI.merge({ keep_id: keepId, merge_id: mergeId });
      toast.success(res.data?.message || 'Merged');
      setDuplicates(prev => prev.filter(d => d.party_a.party_id !== mergeId && d.party_b.party_id !== mergeId));
      loadParties();
    } catch (e) { toast.error(e.response?.data?.detail || 'Merge failed'); }
  };

  return (
    <div data-testid="party-management-page" className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-semibold text-2xl tracking-tight text-[hsl(var(--foreground))]">Parties</h1>
          <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-[hsl(0,0%,55%)] mt-1">Manage clients, vendors, and billing parties</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <>
              <button onClick={handleFindDuplicates} disabled={scanningDups} className="btn-secondary flex items-center gap-2 text-[13px]" data-testid="find-duplicates-btn">
                <ScanSearch className={`w-3.5 h-3.5 ${scanningDups ? 'animate-pulse' : ''}`} /> {scanningDups ? 'Scanning...' : 'Find Duplicates'}
              </button>
              <button onClick={handleSync} disabled={syncing} className="btn-secondary flex items-center gap-2 text-[13px]" data-testid="sync-parties-btn">
                <RefreshCw className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} /> {syncing ? 'Syncing...' : 'Sync from Transactions'}
              </button>
              <button onClick={() => setMergeDialog(true)} className="btn-secondary flex items-center gap-2 text-[13px]" data-testid="merge-parties-btn">
                <Merge className="w-3.5 h-3.5" /> Merge Parties
              </button>
              <button onClick={() => setSettingsDialog(true)} className="btn-secondary flex items-center gap-2 text-[13px]" data-testid="numbering-settings-btn">
                <Settings className="w-3.5 h-3.5" /> Numbering
              </button>
            </>
          )}
          <button onClick={() => { setForm({ name: '', type: 'general', gstin: '', address: '', mobile: '', email: '', state: '' }); setFormDialog({ open: true, editing: null }); }}
            className="btn-accent flex items-center gap-2 text-[13px]" data-testid="new-party-btn">
            <Plus className="w-3.5 h-3.5" /> New Party
          </button>
        </div>
      </div>

      <div className="flex gap-3 bg-white dark:bg-[hsl(0,0%,7.8%)] border border-[hsl(0,0%,90%)] dark:border-[hsl(0,0%,14.9%)] rounded-md p-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[hsl(0,0%,55%)]" />
          <input type="text" placeholder="Search parties..." value={search}
            onChange={(e) => setSearch(e.target.value)} className="form-input pl-9" data-testid="party-search" />
        </div>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="form-input w-40" data-testid="party-type-filter">
          <option value="">All Types</option>
          <option value="general">General</option>
          <option value="client">Client</option>
          <option value="vendor">Vendor</option>
          <option value="consignor">Consignor</option>
          <option value="consignee">Consignee</option>
          <option value="transporter">Transporter</option>
          <option value="broker">Broker</option>
          <option value="supplier">Supplier</option>
        </select>
      </div>

      <div className="bg-white dark:bg-[hsl(0,0%,7.8%)] border border-[hsl(0,0%,90%)] dark:border-[hsl(0,0%,14.9%)] rounded-md overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-20"><div className="w-5 h-5 border-2 border-[hsl(0,0%,3.9%)] dark:border-white border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full data-table" data-testid="party-table">
              <thead><tr><th>Name</th><th>Type</th><th>GSTIN</th><th>Mobile</th><th>Email</th><th>Address</th>{isAdmin && <th>Assigned To</th>}{isAdmin && <th className="text-center">Actions</th>}</tr></thead>
              <tbody>
                {parties.map((p) => (
                  <tr key={p.party_id} data-testid={`party-row-${p.party_id}`}>
                    <td className="font-medium">{p.name}</td>
                    <td><span className="inline-block px-2 py-0.5 text-[10px] rounded border bg-[hsl(0,0%,96%)] text-[hsl(0,0%,40%)] border-[hsl(0,0%,90%)] dark:bg-[hsl(0,0%,12%)] dark:text-[hsl(0,0%,60%)] dark:border-[hsl(0,0%,20%)] capitalize font-medium">{p.type}</span></td>
                    <td className="text-xs font-mono text-[hsl(0,0%,55%)]">{p.gstin || '-'}</td>
                    <td className="text-sm">{p.mobile || '-'}</td>
                    <td className="text-sm">{p.email || '-'}</td>
                    <td className="text-sm text-[hsl(0,0%,55%)] max-w-[200px] truncate">{p.address || '-'}</td>
                    {isAdmin && <td className="text-xs text-slate-500">{p.assigned_to ? (employees.find(e => e.user_id === p.assigned_to)?.name || p.assigned_to) : '-'}</td>}
                    {isAdmin && (
                      <td>
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => { setForm({ name: p.name, type: p.type, gstin: p.gstin || '', address: p.address || '', mobile: p.mobile || '', email: p.email || '', state: p.state || '', assigned_to: p.assigned_to || '' }); setFormDialog({ open: true, editing: p.party_id }); }}
                            className="p-1.5 hover:bg-[hsl(0,0%,96%)] dark:hover:bg-[hsl(0,0%,12%)] rounded-md transition" data-testid={`edit-party-${p.party_id}`}>
                            <Edit className="w-3.5 h-3.5 text-[hsl(0,0%,45%)]" />
                          </button>
                          <button onClick={() => setDeleteDialog({ open: true, id: p.party_id })}
                            className="p-1.5 hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded-md transition" data-testid={`delete-party-${p.party_id}`}>
                            <Trash2 className="w-3.5 h-3.5 text-[hsl(var(--destructive))]" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
                {parties.length === 0 && <tr><td colSpan={isAdmin ? 9 : 6} className="text-center text-[hsl(0,0%,55%)] py-12">No parties found. Create one to get started.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Party Form Dialog */}
      <Dialog open={formDialog.open} onOpenChange={(o) => !o && setFormDialog({ open: false, editing: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading font-semibold tracking-tight">{formDialog.editing ? 'Edit Party' : 'New Party'}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><label className="form-label">Party Name *</label><input type="text" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} className="form-input" data-testid="party-form-name" /></div>
            <div><label className="form-label">Type</label>
              <select value={form.type} onChange={(e) => setForm(f => ({ ...f, type: e.target.value }))} className="form-input" data-testid="party-form-type">
                <option value="general">General</option><option value="client">Client</option><option value="vendor">Vendor</option>
                <option value="consignor">Consignor</option><option value="consignee">Consignee</option>
                <option value="transporter">Transporter</option><option value="broker">Broker</option><option value="supplier">Supplier</option>
              </select>
            </div>
            <div><label className="form-label">GSTIN</label>
              <div className="flex gap-2">
                <input type="text" value={form.gstin} onChange={(e) => setForm(f => ({ ...f, gstin: e.target.value.toUpperCase() }))} className="form-input flex-1" data-testid="party-form-gstin" placeholder="Enter 15-digit GSTIN" maxLength={15} />
                <button type="button" disabled={form.gstin.length !== 15 || gstinLoading} onClick={async () => {
                  setGstinLoading(true);
                  try {
                    const res = await gstinAPI.validate(form.gstin);
                    const d = res.data;
                    if (d.valid) {
                      const updates = {};
                      if (d.trade_name || d.legal_name) {
                        if (!form.name) updates.name = d.trade_name || d.legal_name;
                      }
                      if (d.address && !form.address) updates.address = d.address;
                      if (d.state) updates.state = d.state;
                      setForm(f => ({ ...f, ...updates }));
                      toast.success(`GSTIN verified: ${d.trade_name || d.legal_name || d.state || 'Valid'}`);
                    }
                  } catch (e) { toast.error(e.response?.data?.detail || 'Invalid GSTIN'); }
                  setGstinLoading(false);
                }} className="btn-secondary text-xs px-3 shrink-0 flex items-center gap-1" data-testid="party-gstin-fetch-btn">
                  {gstinLoading ? <RefreshCw className="w-3 h-3 animate-spin" /> : <ScanSearch className="w-3 h-3" />}
                  {gstinLoading ? 'Fetching...' : 'Auto-Fetch'}
                </button>
              </div>
              {form.state && <p className="text-xs text-emerald-600 mt-1">State: {form.state}</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="form-label">Mobile</label><input type="text" value={form.mobile} onChange={(e) => setForm(f => ({ ...f, mobile: e.target.value }))} className="form-input" data-testid="party-form-mobile" /></div>
              <div><label className="form-label">Email</label><input type="text" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} className="form-input" data-testid="party-form-email" /></div>
            </div>
            <div><label className="form-label">Address</label><textarea value={form.address} onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))} className="form-input min-h-[50px] resize-y" data-testid="party-form-address" /></div>
            {isAdmin && employees.length > 0 && (
              <div>
                <label className="form-label">Assign to Employee</label>
                <select value={form.assigned_to || ''} onChange={(e) => setForm(f => ({ ...f, assigned_to: e.target.value }))} className="form-input" data-testid="party-form-assigned-to">
                  <option value="">-- All / Unassigned --</option>
                  {employees.map(emp => (
                    <option key={emp.user_id} value={emp.user_id}>{emp.name} ({emp.role})</option>
                  ))}
                </select>
                <p className="text-[10px] text-slate-400 mt-1">Employee will only see this party in their ledger.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormDialog({ open: false, editing: null })}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="btn-accent" data-testid="party-form-save">{saving ? 'Saving...' : formDialog.editing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge Parties Dialog */}
      <Dialog open={mergeDialog} onOpenChange={(o) => !o && setMergeDialog(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading font-semibold tracking-tight">Merge Parties</DialogTitle></DialogHeader>
          <p className="text-sm text-[hsl(0,0%,55%)]">Select two parties to merge. All transactions from the second party will be transferred to the first.</p>
          <div className="space-y-3 pt-2">
            <div>
              <label className="form-label">Keep This Party (Primary)</label>
              <select value={mergeKeep} onChange={e => setMergeKeep(e.target.value)} className="form-input" data-testid="merge-keep-select">
                <option value="">-- Select party to keep --</option>
                {parties.map(p => <option key={p.party_id} value={p.party_id}>{p.name} ({p.type})</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Merge & Remove This Party</label>
              <select value={mergeDrop} onChange={e => setMergeDrop(e.target.value)} className="form-input" data-testid="merge-drop-select">
                <option value="">-- Select party to merge away --</option>
                {parties.filter(p => p.party_id !== mergeKeep).map(p => <option key={p.party_id} value={p.party_id}>{p.name} ({p.type})</option>)}
              </select>
            </div>
            {mergeKeep && mergeDrop && (
              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-md p-3 text-[12px] text-amber-800 dark:text-amber-400">
                All invoices, receipts, advances, and journal entries referencing "{parties.find(p => p.party_id === mergeDrop)?.name}" will be updated to "{parties.find(p => p.party_id === mergeKeep)?.name}". This action cannot be undone.
              </div>
            )}
          </div>
          <DialogFooter className="pt-3">
            <Button variant="outline" onClick={() => setMergeDialog(false)}>Cancel</Button>
            <Button onClick={handleMerge} disabled={merging || !mergeKeep || !mergeDrop} className="btn-accent" data-testid="merge-confirm-btn">
              {merging ? 'Merging...' : 'Merge Parties'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Numbering Settings Dialog */}
      <Dialog open={settingsDialog} onOpenChange={(o) => !o && setSettingsDialog(false)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading font-semibold tracking-tight">Document Numbering</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-[11px] text-[hsl(0,0%,55%)]">Format: PREFIX/YEAR/SEQUENCE (e.g. VLPL/LR/2026/00001)</p>
            <div><label className="form-label">Receipt Prefix</label><input type="text" value={numbering.receipt_prefix} onChange={(e) => setNumbering(n => ({ ...n, receipt_prefix: e.target.value }))} className="form-input" data-testid="numbering-receipt-prefix" placeholder="VLPL/LR" /></div>
            <div><label className="form-label">Invoice Prefix</label><input type="text" value={numbering.invoice_prefix} onChange={(e) => setNumbering(n => ({ ...n, invoice_prefix: e.target.value }))} className="form-input" data-testid="numbering-invoice-prefix" placeholder="VLPL/INV" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsDialog(false)}>Cancel</Button>
            <Button onClick={saveNumbering} className="btn-accent" data-testid="save-numbering-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(o) => !o && setDeleteDialog({ open: false, id: '' })}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading font-semibold tracking-tight">Delete Party</DialogTitle></DialogHeader>
          <p className="text-sm text-[hsl(0,0%,55%)]">Delete this party? This cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, id: '' })}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="party-delete-confirm">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Detection Dialog */}
      <Dialog open={dupDialog} onOpenChange={(o) => !o && setDupDialog(false)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading font-semibold tracking-tight flex items-center gap-2">
              <ScanSearch className="w-5 h-5 text-[hsl(221,100%,50%)]" /> Duplicate Detection
            </DialogTitle>
          </DialogHeader>
          <p className="text-[12px] text-[hsl(0,0%,55%)]">
            {duplicates.length > 0
              ? `Found ${duplicates.length} potential duplicate pair${duplicates.length > 1 ? 's' : ''}. Choose which party to keep for each match.`
              : 'No potential duplicates found. All party names are sufficiently unique.'}
          </p>
          <div className="flex-1 overflow-y-auto space-y-3 pt-2 pr-1">
            {duplicates.map((dup, i) => (
              <div key={i} className="bg-[hsl(0,0%,98%)] dark:bg-[hsl(0,0%,7%)] border border-[hsl(0,0%,90%)] dark:border-[hsl(0,0%,14.9%)] rounded-md p-4" data-testid={`dup-pair-${i}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] font-bold text-[hsl(0,0%,55%)] uppercase tracking-[0.1em]">
                    {dup.similarity}% Similar
                  </span>
                  <span className={`text-[9px] font-bold px-2 py-0.5 rounded border uppercase tracking-[0.1em] ${
                    dup.similarity >= 90 ? 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-400 dark:border-rose-900'
                    : dup.similarity >= 80 ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-900'
                    : 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-400 dark:border-blue-900'
                  }`}>
                    {dup.similarity >= 90 ? 'Very likely' : dup.similarity >= 80 ? 'Likely' : 'Possible'}
                  </span>
                </div>
                <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-center">
                  {/* Party A */}
                  <div className="border border-[hsl(0,0%,88%)] dark:border-[hsl(0,0%,18%)] rounded-md p-3 bg-white dark:bg-[hsl(0,0%,8%)]">
                    <p className="text-sm font-semibold text-[hsl(var(--foreground))] mb-0.5">{dup.party_a.name}</p>
                    <p className="text-[10px] text-[hsl(0,0%,55%)]">{dup.party_a.type || 'general'} {dup.party_a.gstin ? `| ${dup.party_a.gstin}` : ''}</p>
                    <button onClick={() => handleQuickMerge(dup.party_a.party_id, dup.party_b.party_id)}
                      className="mt-2 w-full btn-primary text-[11px] py-1.5 flex items-center justify-center gap-1.5"
                      data-testid={`keep-a-${i}`}>
                      <Check className="w-3 h-3" /> Keep this
                    </button>
                  </div>

                  <ArrowRight className="w-4 h-4 text-[hsl(0,0%,65%)] shrink-0" />

                  {/* Party B */}
                  <div className="border border-[hsl(0,0%,88%)] dark:border-[hsl(0,0%,18%)] rounded-md p-3 bg-white dark:bg-[hsl(0,0%,8%)]">
                    <p className="text-sm font-semibold text-[hsl(var(--foreground))] mb-0.5">{dup.party_b.name}</p>
                    <p className="text-[10px] text-[hsl(0,0%,55%)]">{dup.party_b.type || 'general'} {dup.party_b.gstin ? `| ${dup.party_b.gstin}` : ''}</p>
                    <button onClick={() => handleQuickMerge(dup.party_b.party_id, dup.party_a.party_id)}
                      className="mt-2 w-full btn-primary text-[11px] py-1.5 flex items-center justify-center gap-1.5"
                      data-testid={`keep-b-${i}`}>
                      <Check className="w-3 h-3" /> Keep this
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter className="pt-3">
            <Button variant="outline" onClick={() => setDupDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
